import React, { Fragment, lazy } from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { compose, find, propOr } from "ramda";
import { format } from "date-fns";
import Header from "../Header/container";
import Footer from "../Footer";
import Modals from "../Modals/container";
import {
  APPLICATIONS,
  CONNECTORS_OVERVIEW_SORT_PROPERTIES,
  FS_COUNTRIES,
  FS_COUNTRIES_AND_PROFILES,
  GAQ_COUNTRIES,
  RELEASE_NOTE_ADMIN_MODE,
  ROLLOUT_SORT_PROPERTIES,
  ROUTES,
  SORT_DIRECTIONS,
  ALLOWED_CONFIGURATION_QUALIFIERS,
} from "../../utils/constants";
import BackToTop from "../BackToTop";
import ProtectedRoute from "../ProtectedRoute/container";
// Lazy load components
const ReleaseTimeline = lazy(() => import("../ReleaseTimeline/container"));
const ReleaseNoteFromApi = lazy(() => import("../ReleaseNotes/container"));
const AddUpdateReleaseNote = lazy(() =>
  import("../ReleaseNotes/AddUpdateReleaseNote/container")
);
const ProjectOverview = lazy(() =>
  import("../RolloutProgress/ProjectOverview/container")
);
const BrowserMatrix = lazy(() => import("../BrowserMatrix"));
const Release = lazy(() => import("../Release/container"));
const FourOhFour = lazy(() => import("../FourOhFour"));
const FSCountryConfigurations = lazy(() =>
  import("../CountryConfigurations/FrequentShipment/container")
);
const GAQCountryConfigurations = lazy(() =>
  import("../CountryConfigurations/GetAQuote/container")
);
const CJRolloutProgress = lazy(() =>
  import("../RolloutProgress/CustomerJourney/container")
);
const HomepageRolloutProgress = lazy(() =>
  import("../RolloutProgress/Homepage/container")
);
const ReleaseNoteAdmin = lazy(() => import("../ReleaseNotes/Admin/container"));
const ConnectorsOverview = lazy(() =>
  import("../ConnectorsOverview/container")
);

const LATEST_UPDATE_DATE = new Date(process.env.LATEST_UPDATE);

// Wrapper components for route parameters
const CJRolloutProgressWrapper = () => {
  const { application } = useParams();
  const [searchParams] = useSearchParams();
  const region = searchParams.get("region");
  const sortProperty =
    searchParams.get("sortProperty") || ROLLOUT_SORT_PROPERTIES.COUNTRY;
  const sortDirection =
    searchParams.get("sortDirection") || SORT_DIRECTIONS.ASC;

  return (
    <CJRolloutProgress
      application={application}
      filteredRegion={region}
      sortProperty={sortProperty}
      sortDirection={sortDirection}
    />
  );
};

const ProjectOverviewWrapper = () => {
  const { view } = useParams();
  const [searchParams] = useSearchParams();
  const detail = searchParams.get("detail");

  return <ProjectOverview detail={detail} view={view} />;
};

const HomepageRolloutProgressWrapper = () => {
  const { version } = useParams();
  const [searchParams] = useSearchParams();
  const region = searchParams.get("region");
  const sortProperty =
    searchParams.get("sortProperty") || ROLLOUT_SORT_PROPERTIES.COUNTRY;
  const sortDirection =
    searchParams.get("sortDirection") || SORT_DIRECTIONS.ASC;

  return (
    <HomepageRolloutProgress
      version={version}
      filteredRegion={region}
      sortProperty={sortProperty}
      sortDirection={sortDirection}
    />
  );
};

const FSCountryConfigurationsWrapper = () => {
  const { country } = useParams();
  const [searchParams] = useSearchParams();
  const qualifier =
    ALLOWED_CONFIGURATION_QUALIFIERS.find(
      (el) => el === searchParams.get("qualifier")
    ) || "FS";
  const profile = compose(
    find((el) => el === searchParams.get("profile")),
    propOr([], country)
  )(FS_COUNTRIES_AND_PROFILES);

  if (!FS_COUNTRIES.some(([code]) => code === country)) {
    return <FourOhFour />;
  }

  return (
    <FSCountryConfigurations
      country={country}
      qualifier={qualifier}
      profile={profile}
    />
  );
};

const GAQCountryConfigurationsWrapper = () => {
  const { country } = useParams();

  if (!GAQ_COUNTRIES.some(([code]) => code === country)) {
    return <FourOhFour />;
  }

  return <GAQCountryConfigurations country={country} />;
};

const ConnectorsOverviewWrapper = () => {
  const [searchParams] = useSearchParams();
  const sortProperty =
    searchParams.get("sortProperty") ||
    CONNECTORS_OVERVIEW_SORT_PROPERTIES.DATE;
  const sortDirection =
    searchParams.get("sortDirection") || SORT_DIRECTIONS.DESC;

  return (
    <ConnectorsOverview
      sortProperty={sortProperty}
      sortDirection={sortDirection}
    />
  );
};

const Application = () => (
  <Fragment>
    <Header latestUpdateDate={LATEST_UPDATE_DATE} />
    <Routes>
      <Route
        path="/"
        element={
          <Navigate
            to={`${ROUTES.CJ_ROLLOUT_OVERVIEW}/${APPLICATIONS.ALL}`}
            replace
          />
        }
      />
      <Route
        path={ROUTES.CJ_ROLLOUT_OVERVIEW}
        element={
          <Navigate
            to={`${ROUTES.CJ_ROLLOUT_OVERVIEW}/${APPLICATIONS.ALL}`}
            replace
          />
        }
      />
      <Route
        path={ROUTES.HOMEPAGE_ROLLOUT_OVERVIEW}
        element={
          <Navigate
            to={`${ROUTES.HOMEPAGE_ROLLOUT_OVERVIEW}/${APPLICATIONS.ALL}`}
            replace
          />
        }
      />
      <Route
        path={ROUTES.FS_COUNTRY_CONFIGURATIONS}
        element={
          <Navigate to={`${ROUTES.FS_COUNTRY_CONFIGURATIONS}/DE`} replace />
        }
      />
      <Route
        path={ROUTES.GAQ_COUNTRY_CONFIGURATIONS}
        element={
          <Navigate to={`${ROUTES.GAQ_COUNTRY_CONFIGURATIONS}/AU`} replace />
        }
      />
      <Route
        path="/rollout-overview/:path"
        element={<Navigate to="/rollout-progress/:path" replace />}
      />
      <Route
        path={ROUTES.PROJECT_OVERVIEW}
        element={
          <Navigate
            to={`${ROUTES.PROJECT_OVERVIEW}/${format(new Date(), "Y")}`}
            replace
          />
        }
      />

      <Route path={ROUTES.BROWSER_MATRIX} element={<BrowserMatrix />} />
      <Route
        path={`${ROUTES.CJ_ROLLOUT_OVERVIEW}/:application`}
        element={<CJRolloutProgressWrapper />}
      />
      <Route
        path={`${ROUTES.PROJECT_OVERVIEW}/:view`}
        element={<ProjectOverviewWrapper />}
      />
      <Route
        path={`${ROUTES.HOMEPAGE_ROLLOUT_OVERVIEW}/:version`}
        element={<HomepageRolloutProgressWrapper />}
      />
      <Route
        path={`${ROUTES.FS_RELEASE_NOTES}/:version?`}
        element={
          <ReleaseTimeline application={APPLICATIONS.FREQUENT_SHIPMENT} />
        }
      />
      <Route
        path={`${ROUTES.FSX_RELEASE_NOTES}/:version?`}
        element={
          <ReleaseTimeline application={APPLICATIONS.FREQUENT_SHIPMENT_FSX} />
        }
      />
      <Route
        path={`${ROUTES.GAQ_RELEASE_NOTES}/:version?`}
        element={<ReleaseNoteFromApi application={APPLICATIONS.GET_A_QUOTE} />}
      />
      <Route
        path={ROUTES.GAQ_RELEASE_NOTES_ADMIN}
        element={
          <ProtectedRoute>
            <ReleaseNoteAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GAQ_ADD_RELEASE_NOTE}
        element={
          <ProtectedRoute>
            <AddUpdateReleaseNote mode={RELEASE_NOTE_ADMIN_MODE.ADD} />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.GAQ_UPDATE_RELEASE_NOTE}/:id`}
        element={
          <ProtectedRoute>
            <AddUpdateReleaseNote mode={RELEASE_NOTE_ADMIN_MODE.UPDATE} />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.SHIP_NOW_RELEASE_NOTES}/:version?`}
        element={<ReleaseTimeline application={APPLICATIONS.SHIP_NOW} />}
      />
      <Route path={`${ROUTES.FS_RELEASE}/:version`} element={<Release />} />
      <Route
        path={`${ROUTES.FS_COUNTRY_CONFIGURATIONS}/:country`}
        element={<FSCountryConfigurationsWrapper />}
      />
      <Route
        path={`${ROUTES.GAQ_COUNTRY_CONFIGURATIONS}/:country`}
        element={<GAQCountryConfigurationsWrapper />}
      />
      <Route
        path={ROUTES.CONNECTORS_OVERVIEW}
        element={<ConnectorsOverviewWrapper />}
      />
      <Route path="*" element={<FourOhFour />} />
    </Routes>
    <Footer />
    <BackToTop />
    <Modals />
  </Fragment>
);

export default Application;
